import React from 'react';
import { styled } from '@mui/material/styles';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import ResetPasswordForm from '../../components/auth/ResetPasswordForm';
import authService from '../../services/authService';
import { useTranslation } from 'react-i18next';

const PREFIX = 'ResetPassword';

const classes = {
  container: `${PREFIX}-container`,
  content: `${PREFIX}-content`,
  left: `${PREFIX}-left`,
  right: `${PREFIX}-right`,
  center: `${PREFIX}-center`,
  logo1: `${PREFIX}-logo1`,
  logo2: `${PREFIX}-logo2`,
  logo3: `${PREFIX}-logo3`,
  logo4: `${PREFIX}-logo4`,
};

const Root = styled('div')(({ theme }) => ({
  [`&.${classes.container}`]: {
    display: 'flex',
    flexDirection: 'row',
    flex: '0 1 100%',
    backgroundColor: theme.palette.info,
    width: '100%',
    height: '100%',
    backgroundSize: 'cover',
    [theme.breakpoints.down('sm')]: {
      backgroundImage: 'url(/background_small.png)',
    },
    [theme.breakpoints.up('sm')]: {
      backgroundImage: 'url(/background.png)',
    },
  },

  [`& .${classes.left}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    marginTop: 'auto',
    marginBottom: 'auto',
    marginRight: 65,
    alignItems: 'end',
  },

  [`& .${classes.right}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    marginTop: 'auto',
    marginBottom: 'auto',
    marginLeft: 65,
    alignItems: 'left',
  },

  [`& .${classes.center}`]: {
    flexGrow: 1,
    display: 'flex',
    flexDirection: 'column',
    marginTop: 'auto',
    marginBottom: 'auto',
    alignItems: 'center',
  },

  [`& .${classes.logo1}`]: {
    width: 400,
    marginBottom: 19,
  },

  [`& .${classes.logo2}`]: {
    width: 230,
    marginTop: 18,
    marginRight: 85,
  },

  [`& .${classes.logo3}`]: {
    width: 163,
    marginBottom: 19,
  },

  [`& .${classes.logo4}`]: {
    width: 137,
    marginTop: 18,
  },
}));

export default function ResetPassword({ activate }: { activate: boolean }) {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const isLarge = useMediaQuery(theme.breakpoints.up('md'));
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [email, setEmail] = React.useState('');
  const [resetError, setResetError] = React.useState<string>();
  const [returnPage, setReturnPage] = React.useState<string>('/');

  React.useEffect(() => {
    const page = searchParams.get('returnPage');
    if (page) {
      setReturnPage(page);
    }
    setEmail(searchParams.get('email') || '');
    setSearchParams({});
  }, []);

  const activateAccount = (data: any) => {
    authService
      .activateAccount(data.Password)
      .then(() => {
        navigate('/');
      })
      .catch((err) => {
        setResetError(t('login.resetFailed') || '');
      });
  };

  const resetPassword = (data: any) => {
    authService
      .resetPassword(data.Email, data.Code, data.Password)
      .then(() => {
        navigate('/');
      })
      .catch((err) => {
        setResetError(t('login.invalid') || '');
      });
  };

  const submit = (info: any) => {
    if (activate) {
      activateAccount(info);
    } else {
      resetPassword(info);
    }
  };

  return (
    <Root className={classes.container}>
      {isLarge && (
        <Box className={classes.left}>
          <img alt="RideCafe" src="/logo.png" className={classes.logo1} />
          <img
            alt="AmericanLogistics"
            src="/logo2.png"
            className={classes.logo2}
          />
        </Box>
      )}
      {isLarge && (
        <Box className={classes.right}>
          <ResetPasswordForm
            activate={activate}
            email={email}
            submit={submit}
            error={resetError}
          />
        </Box>
      )}
      {!isLarge && (
        <Box className={classes.center}>
          <img alt="RideCafe" src="/logo.png" className={classes.logo3} />
          <ResetPasswordForm
            activate={activate}
            email={email}
            submit={submit}
            error={resetError}
          />
          <img
            alt="AmericanLogistics"
            src="/logo2.png"
            className={classes.logo4}
          />
        </Box>
      )}
    </Root>
  );
}
